import React, { useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAuth, useToggle, useIntl, useConfirm, useFileDownload, useUpdateEffect } from 'hooks'
import { axiosApi } from 'services/api'
import { useGetSharedSummaryQuery } from 'services'
import SearchAndFilter from 'features/collection-searchfilter/SearchAndFilter'
import Edit from 'features/collection/Edit'
import Share from 'features/share/Share'
import CollectionListItem from 'components/Collection/CollectionListItem'
import CollectionsListFallback from 'components/Fallback/ListBar'
import { SidebarLoader } from 'components/Loaders'
import { ScrollableDirective } from 'components/Zui'
import {
    getCollections,
    collectionSelector,
    setCollection,
    updateCollection,
    deleteCollection,
} from './collectionSlice'
import './index.css'

const List = ({ selectedCollection }) => {
    const { t } = useIntl()
    const dispatch = useDispatch()
    const { getConfirmation } = useConfirm()
    const { isAuthorized } = useAuth()
    const [editableCollection, setEditableCollection] = useState()
    const [shareableCollection, setShareableCollection] = useState()
    const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useToggle()
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useToggle()
    const collectionsCount = useSelector(state => collectionSelector.selectTotal(state))
    const collections = useSelector(state => collectionSelector.selectAll(state))
    const { isLoading, isError, filterParams, sort } = useSelector(state => state.collections)
    const { summary: favSummary, latestDateTime: favUpdatedDateTime } = useSelector(
        state => state.favorites
    )

    const { data: sharedSummary } = useGetSharedSummaryQuery()
    const { download } = useFileDownload('collection')

    useUpdateEffect(() => {
        if (isAuthorized) {
            dispatch(axiosApi.util.invalidateTags(['CollectionFiles']))
            dispatch(getCollections())
        }
    }, [filterParams])

    const extCollections = useMemo(() => {
        return sort === 'dateAsc' || sort === 'dateDesc'
            ? collections
                  .filter(c => !c.isNew)
                  .slice()
                  .sort((a, b) =>
                      sort === 'dateAsc'
                          ? new Date(a.updatedDateTime) - new Date(b.updatedDateTime)
                          : new Date(b.updatedDateTime) - new Date(a.updatedDateTime)
                  )
            : collections.filter(c => !c.isNew)
    }, [collections, sort])

    const sortedCollections = useMemo(
        () => [
            ...collections
                .filter(c => c.isNew)
                .slice()
                .sort((a, b) => new Date(b.updatedDateTime) - new Date(a.updatedDateTime)),
            ...extCollections,
        ],
        [collections, sort]
    )

    const handleSelectCollection = id => {
        dispatch(setCollection(id))
    }

    const handleShareCollection = collection => {
        const count = collections.find(c => c.id === collection.id).fileCount
        if (!count) return

        setShareableCollection({
            type: 'collection',
            id: collection.id,
            name: collection.name,
        })
        onShareOpen()
    }

    const handleDownload = id => {
        const count = collections.find(c => c.id === id).fileCount
        if (!count) return

        download({ id })
    }

    const handleEditCollection = collection => {
        setEditableCollection(collection)
        onEditOpen()
    }

    const handleUpdateCollection = values => {
        dispatch(updateCollection({ id: values.id, data: { collectionName: values.name } }))
        onEditClose()
    }

    const handleDeleteCollection = async collection => {
        const confirmed = await getConfirmation({
            title: t('collection.confirm.delete.title'),
            message: t('collection.confirm.delete.message'),
            buttons: [t('cancel'), t('delete')],
        })
        if (confirmed) {
            dispatch(axiosApi.util.invalidateTags(['CollectionSuggestions']))
            dispatch(deleteCollection(collection.id))
        }
    }

    const CONTEXT_MENU_ITEMS = [
        {
            title: t('share'),
            icon: 'share',
            id: 'share',
        },
        {
            title: t('download'),
            icon: 'downloadIcnDark',
            id: 'download',
        },
        {
            title: t('delete'),
            icon: 'delete',
            id: 'delete',
        },
        {
            title: t('edit'),
            icon: 'edit',
            id: 'edit',
        },
    ]

    const hasNoCollections = () => {
        return !isLoading && !collectionsCount && !isError
    }

    const isInitLoading = () => {
        return isLoading && !collectionsCount
    }

    return (
        <>
            <SearchAndFilter />
            <div className="collection-count-clear-filter-wrapper">
                <div className="patients-count">
                    {collectionsCount} {collectionsCount > 1 ? t('collections') : t('collection')}
                </div>
            </div>
            <div className="sticky">
                {isAuthorized && (
                    <CollectionListItem
                        collection={{
                            name: t('all.favorites'),
                            fileCount: favSummary.all || t('no'),
                            updatedDateTime: favUpdatedDateTime,
                            id: 'favorites',
                        }}
                        onClick={() => handleSelectCollection('favorites')}
                    />
                )}
                <CollectionListItem
                    collection={{
                        name: t('shared.with.me'),
                        fileCount: sharedSummary?.all || 0,
                        updatedDateTime: sharedSummary?.all ? sharedSummary.shareDate : null,
                        id: 'shared',
                    }}
                    onClick={() => handleSelectCollection('shared')}
                />
            </div>
            <div className="collection-list-wrapper">
                {isInitLoading() && <SidebarLoader />}
                {hasNoCollections() && (
                    <CollectionsListFallback title={t('fallback.no.collections')} icon="folder" />
                )}
                {isError && (
                    <CollectionsListFallback
                        title={t('fallback.error.collections')}
                        icon="folder"
                    />
                )}
                {collectionsCount > 0 && (
                    <ScrollableDirective
                        style={{
                            maxHeight: '100%',
                            width: '100%',
                            flexGrow: '1',
                            overflow: 'auto',
                        }}
                    >
                        {sortedCollections.length > 0 &&
                            sortedCollections.map(collection => {
                                return (
                                    <CollectionListItem
                                        key={collection.id}
                                        onClick={e => handleSelectCollection(e)}
                                        handleEditCollection={handleEditCollection}
                                        handleDeleteCollection={handleDeleteCollection}
                                        handleShareCollection={handleShareCollection}
                                        handleDownload={handleDownload}
                                        selectedCollection={selectedCollection}
                                        collection={collection}
                                        contextMenu={CONTEXT_MENU_ITEMS}
                                    />
                                )
                            })}
                    </ScrollableDirective>
                )}
            </div>
            {isShareOpen && <Share onClose={onShareClose} data={shareableCollection} />}
            {isEditOpen && (
                <Edit
                    onClose={onEditClose}
                    data={editableCollection}
                    onSubmit={handleUpdateCollection}
                />
            )}
        </>
    )
}

export default List
